/**
 * هندسه‌ی زنجیره‌ی دومینو روی فرشِ میز — آینه‌ی FitChain / LayoutChain / Walk ِ DominoView ِ اپ، تا مار
 * روی وب و گوشی یک شکل باشد.
 *
 * مختصات: مرکزِ فرش (۰، ۰)، y رو به **بالا** (مثلِ یونیتی)؛ صفحه هنگامِ کشیدن برمی‌گرداند. S ضلعِ کوتاهِ
 * مهره است (مهره‌ی خوابیده 2S × S).
 *
 * زنجیره از مرکز دو سو می‌رود؛ ردیف‌ها 2S از هم فاصله دارند تا جفتِ چلیپا (بلندیِ 2S) جا شود. به لبه که
 * رسید، مهره‌ی گوشه ایستاده زیرِ (سمتِ راست) یا بالای (سمتِ چپ) نیمه‌ی بیرونیِ مهره‌ی قبلی می‌نشیند و
 * ردیفِ بعد برعکس می‌رود. جفت فقط وقتی چلیپاست که بعدش یک مهره‌ی دیگر هم در همان ردیف جا شود. بعد
 * زنجیره وسط می‌آید: ردیفِ مرکز به سمتِ کوتاه‌تر می‌لغزد و کلِ مار در بلندی وسطِ فرش می‌نشیند.
 */

/** مهره‌ی نشسته: inner به زنجیره چسبیده، outer سرِ باز است. */
export type Placed = { a: number; b: number; inner: number; outer: number };
export type Pose = { x: number; y: number; vert: boolean; v1: number; v2: number; side: -1 | 0 | 1; index: number };
/** اندازه‌ی فرش و شعاعِ گوشه‌اش (px). */
export type Felt = { w: number; h: number; r: number };
export type Chain = { center: Placed | null; left: Placed[]; right: Placed[] };
export type Fit = { S: number; poses: Pose[]; ox: number; oy: number };

const MARGIN = 10;
const isDouble = (p: Placed) => p.a === p.b;

/** زنجیره‌ی دیده‌شده (چپ به راست) + شماره‌ی مهره‌ی مرکز → مرکز و دو سو (extra[0] ِ سرور). */
export function splitChain(chain: readonly (readonly [number, number])[], center: number): Chain {
  if (chain.length === 0) return { center: null, left: [], right: [] };
  const c = Math.max(0, Math.min(chain.length - 1, center));
  const [ca, cb] = chain[c]!;
  const left: Placed[] = [], right: Placed[] = [];
  for (let j = c - 1; j >= 0; j--) { const [l, r] = chain[j]!; left.push({ a: l, b: r, inner: r, outer: l }); }
  for (let j = c + 1; j < chain.length; j++) { const [l, r] = chain[j]!; right.push({ a: l, b: r, inner: l, outer: r }); }
  return { center: { a: ca, b: cb, inner: ca, outer: cb }, left, right };
}

/** بیشترین فاصله‌ی افقی از مرکز برای ردیفی در y — گوشه‌ی گردِ فرش هم حساب. */
function rowBound(f: Felt, y: number, hh: number): number {
  const ex = f.w / 2 - MARGIN, ey = f.h / 2 - MARGIN, r = Math.max(0, f.r - MARGIN);
  const k = Math.abs(y) + hh - (ey - r);
  if (k <= 0) return ex;
  if (k >= r) return ex - r;
  return ex - r + Math.sqrt(r * r - k * k);
}

export function fits(f: Felt, p: Pose, S: number): boolean {
  const hw = p.vert ? S / 2 : S, hh = p.vert ? S : S / 2;
  const ex = f.w / 2 - MARGIN, ey = f.h / 2 - MARGIN, r = Math.max(0, f.r - MARGIN);
  const ax = Math.abs(p.x) + hw, ay = Math.abs(p.y) + hh;
  if (ax > ex + 0.5 || ay > ey + 0.5) return false;
  const kx = ax - (ex - r), ky = ay - (ey - r);
  return !(kx > 0 && ky > 0 && kx * kx + ky * ky > r * r + 1);
}

function walk(f: Felt, chain: Placed[], side: 0 | 1, x: number, y: number, dir: number, turn: number, S: number, into: Pose[], prevHH: number): boolean {
  let ok = true;
  const L = 2 * S;
  for (let i = 0; i < chain.length; i++) {
    const pd = chain[i]!;
    const bx = rowBound(f, y, S);
    const room = (len: number) => Math.abs(x + dir * len) <= bx;
    let p: Pose;
    if (isDouble(pd) && room(S + L)) {
      p = { x: x + dir * S / 2, y, vert: true, v1: pd.inner, v2: pd.outer, side, index: i };
      x += dir * S;
      prevHH = S;
    } else if (room(L)) {
      const fwd = dir > 0;
      p = { x: x + dir * S, y, vert: false, v1: fwd ? pd.inner : pd.outer, v2: fwd ? pd.outer : pd.inner, side, index: i };
      x += dir * L;
      prevHH = S / 2;
    } else {
      // گوشه زیر/بالای نیمه‌ی بیرونیِ مهره‌ی قبلی؛ اگر آن جفتِ چلیپا بود (بلندتر)، گوشه آن‌قدر دورتر که رویش نیفتد
      const down = turn < 0;
      p = { x: x - dir * S / 2, y: y + turn * (prevHH + S), vert: true, v1: down ? pd.inner : pd.outer, v2: down ? pd.outer : pd.inner, side, index: i };
      y += turn * (prevHH + 1.5 * S);
      x -= dir * S;
      dir = -dir;
      prevHH = S / 2;
    }
    ok = fits(f, p, S) && ok;
    into.push(p);
  }
  return ok;
}

/** چیدن با S و مرکزِ (ox، oy)؛ false اگر جایی از فرش بیرون زد. */
export function layoutAt(f: Felt, c: Chain, S: number, ox: number, oy: number, into: Pose[]): boolean {
  into.length = 0;
  if (!c.center) return true;
  const dbl = isDouble(c.center);
  const p0: Pose = { x: ox, y: oy, vert: dbl, v1: c.center.inner, v2: c.center.outer, side: -1, index: 0 };
  into.push(p0);
  let ok = fits(f, p0, S);
  const half = dbl ? S / 2 : S;
  const hh = dbl ? S : S / 2;
  ok = walk(f, c.right, 1, ox + half, oy, +1, -1, S, into, hh) && ok;
  ok = walk(f, c.left, 0, ox - half, oy, -1, +1, S, into, hh) && ok;
  return ok;
}

const straight = (side: Placed[], S: number) => side.reduce((n, p) => n + (isDouble(p) ? S : 2 * S), 0);

/** بزرگ‌ترین S که زنجیره با آن جا می‌شود، وسط‌نشسته. */
export function fitChain(f: Felt, c: Chain): Fit {
  let S = Math.min(58, f.w / 11.5);
  const poses: Pose[] = [];
  for (let k = 0; k < 14; k++, S *= 0.9) {
    if (!layoutAt(f, c, S, 0, 0, poses)) continue;
    if (!c.center) return { S, poses, ox: 0, oy: 0 };
    const half = isDouble(c.center) ? S / 2 : S;
    const bx = rowBound(f, 0, S);
    const lw = straight(c.left, S), rw = straight(c.right, S);
    // سرِ کوتاه کاملاً در ردیفِ مرکز بماند — با ۲ پیکسل جا، تا گردکردن آن را به گوشه نیندازد
    const shift = Math.trunc(Math.min(Math.max((lw - rw) / 2, -Math.max(0, bx - half - lw - 2)), Math.max(0, bx - half - rw - 2)));
    let ox = shift, oy = 0;
    if (shift !== 0 && !layoutAt(f, c, S, ox, 0, poses)) { ox = 0; layoutAt(f, c, S, 0, 0, poses); }
    let lo = Infinity, hi = -Infinity;
    for (const p of poses) { const hh = p.vert ? S : S / 2; lo = Math.min(lo, p.y - hh); hi = Math.max(hi, p.y + hh); }
    const dy = Math.round(-(lo + hi) / 2);
    if (Math.abs(dy) > 0.5) {
      if (layoutAt(f, c, S, ox, dy, poses)) oy = dy;
      else layoutAt(f, c, S, ox, 0, poses);
    }
    return { S, poses, ox, oy };
  }
  layoutAt(f, c, S, 0, 0, poses);
  return { S, poses, ox: 0, oy: 0 };
}

/**
 * جای مهره‌ی (a، b) اگر روی سرِ side بنشیند (−۱ = اولین مهره). با `here` همان اندازه و جای زنجیره‌ی روی
 * میز — سایه‌ی انتخابِ سر دقیقاً کنارِ سرِ باز؛ بی آن زنجیره‌ی وسط‌آمده‌ی بعدی (جای فرودِ مهره).
 */
export function poseIfPlayed(f: Felt, c: Chain, a: number, b: number, side: number, leftEnd: number, rightEnd: number, here?: Fit): { pose: Pose; S: number } {
  let next: Chain, want = 0, sd: -1 | 0 | 1 = -1;
  if (!c.center || side < 0) {
    next = { center: { a, b, inner: a, outer: b }, left: [], right: [] };
  } else {
    const inner = side === 1 ? rightEnd : leftEnd;
    const pd: Placed = { a, b, inner, outer: a === inner ? b : a };
    if (side === 1) { next = { ...c, right: [...c.right, pd] }; want = next.right.length - 1; sd = 1; }
    else { next = { ...c, left: [...c.left, pd] }; want = next.left.length - 1; sd = 0; }
  }
  let S: number, poses: Pose[];
  if (here && c.center) { S = here.S; poses = []; layoutAt(f, next, S, here.ox, here.oy, poses); }
  else ({ S, poses } = fitChain(f, next));
  const pose = poses.find((p) => p.side === sd && (sd < 0 || p.index === want))
    ?? { x: 0, y: 0, vert: false, v1: a, v2: b, side: sd, index: want };
  return { pose, S };
}

/** مستطیلِ یک مهره (y رو به بالا). */
export function poseRect(p: Pose, S: number): { x0: number; y0: number; x1: number; y1: number } {
  const hw = p.vert ? S / 2 : S, hh = p.vert ? S : S / 2;
  return { x0: p.x - hw, y0: p.y - hh, x1: p.x + hw, y1: p.y + hh };
}
